/**
 * Cubic easing function for smooth animation transitions.
 * Provides easeInOut effect with cubic timing.
 *
 * @param {number} t - Progress value between 0 and 1
 * @returns {number} Eased value between 0 and 1
 */
const cubicEaseInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/**
 * Smoothly scrolls the viewport to center on a specific slice card element.
 * Uses requestAnimationFrame to ensure the scroll animation isn't interrupted by rerenders,
 * which occurs when using native scroll functions (e.g. `scrollIntoView`, `scrollTo`, etc.).
 *
 * @param {React.MutableRefObject<Object>} sliceCardRefs - Object containing refs to all slice cards
 * @param {string|number} sliceId - ID of the slice card to scroll to
 * @returns {void}
 */
const scrollToSliceCard = (sliceCardRefs, sliceId) => {
    requestAnimationFrame(() => {
        const element = sliceCardRefs.current[sliceId];

        const yOffset = -150;
        const startPosition = window.scrollY;
        const targetPosition = element.getBoundingClientRect().top + startPosition + yOffset;
        const distance = targetPosition - startPosition;
        const duration = 400;

        let startTime = null;

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easedProgress = cubicEaseInOut(progress);

            window.scrollTo(0, startPosition + distance * easedProgress);

            // If the animation is not complete, continue the animation
            if (timeElapsed < duration) {
                requestAnimationFrame(animate);
            }
        };

        // Start the animation
        requestAnimationFrame(animate);
    });
};

export default scrollToSliceCard;
